import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProgressionSkillsForSport } from '../data/progression/skills';
import { buildStudentProgressSummary } from '../utils/progressTracking';
import {
  buildInstructorLessonPlan,
  LESSON_OUTCOME_STATUS_OPTIONS,
  LESSON_SKILL_CONFIDENCE_OPTIONS,
  LESSON_STAGE_COMPLETION_OPTIONS,
} from '../utils/instructorLessonBuilder';
import { buildInstructorLessonPath } from '../utils/instructorLessonPath';
import { normalizeLessonInstanceRecord } from '../utils/lessonInstancePersistence';
import { saveLessonBuilderLesson } from '../utils/lessonBuilderWriter';
import { normalizeSportName } from '../utils/drillTemplates';
import { canUseAIAssistDrafts, isTeachingMembership } from '../utils/platformAccess';
import { getOrganizationMemberships } from '../utils/organizationContext';
import AiCopilotPanel from '../components/ai/copilot/AiCopilotPanel';
import { buildClientSignalsFromWorkspace } from '../utils/clientSignals';

const SPORT_OPTIONS = [
  { key: 'skiing', label: 'Skiing' },
  { key: 'snowboarding', label: 'Snowboarding' },
];

function createDocRecord(docSnap) {
  return { id: docSnap.id, ...docSnap.data() };
}

function getDominantSport(videos = []) {
  const counts = new Map();
  (videos || []).forEach((video) => {
    const sport = normalizeSportName(video?.sport || '');
    if (!sport) return;
    counts.set(sport, (counts.get(sport) || 0) + 1);
  });

  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] || 'skiing';
}

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function InstructorLessonBuilderPage() {
  const { currentUser, userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryVideoId = String(params.get('videoId') || '').trim();
  const queryStudentId = String(params.get('studentId') || '').trim();
  const querySport = normalizeSportName(params.get('sport') || '');
  const querySkillId = String(params.get('skill') || '').trim();
  const queryLessonId = String(params.get('lessonId') || '').trim();

  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState(queryStudentId);
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [videos, setVideos] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [selectedSport, setSelectedSport] = useState(querySport || '');
  const [selectedSkillId, setSelectedSkillId] = useState(querySkillId || '');
  const [hasManualSkillSelection, setHasManualSkillSelection] = useState(Boolean(querySkillId));
  const [lessonPlan, setLessonPlan] = useState(null);
  const [lessonInstanceId, setLessonInstanceId] = useState(queryLessonId);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);
  // Pass 70B — a STABLE per-draft lesson id, generated once and reused, so a double-tap / retry lands on the
  // same document (the writer's transactional get-or-create then guarantees a single standalone lesson).
  const [draftLessonId, setDraftLessonId] = useState('');

  useEffect(() => {
    const fetchLessonContext = async () => {
      if (!currentUser?.uid) return;

      setLoading(true);
      setLoadError('');
      try {
        let baseVideo = null;
        let resolvedStudentId = queryStudentId;
        let resolvedSport = querySport;
        let loadedLesson = null;

        if (queryLessonId) {
          const lessonSnap = await getDoc(doc(db, 'lessonInstances', queryLessonId));
          if (lessonSnap.exists()) {
            loadedLesson = normalizeLessonInstanceRecord(lessonSnap, queryLessonId);
            resolvedStudentId = resolvedStudentId || loadedLesson.studentId || '';
            resolvedSport = resolvedSport || loadedLesson.sport || '';
            setLessonInstanceId(loadedLesson.lessonId);
            setSelectedSkillId(loadedLesson.targetSkillId || querySkillId || '');
            setHasManualSkillSelection(Boolean(loadedLesson.targetSkillId || querySkillId));
            setStudentName(String(loadedLesson.studentName || '').trim());
            setStudentEmail(String(loadedLesson.studentEmail || '').trim());
          } else {
            setLoadError('We could not find that saved lesson. A new lesson draft has been opened instead.');
            setLessonInstanceId('');
          }
        } else {
          setLessonInstanceId('');
        }

        if (queryVideoId) {
          const videoSnap = await getDoc(doc(db, 'videos', queryVideoId));
          if (videoSnap.exists()) {
            baseVideo = createDocRecord(videoSnap);
            resolvedStudentId = resolvedStudentId || baseVideo.userId || baseVideo.studentId || '';
            resolvedSport = resolvedSport || normalizeSportName(baseVideo.sport || '');
          }
        }

        const videoResults = [];
        const seenIds = new Set();

        const appendDocs = (snapshot) => {
          snapshot.docs.forEach((docSnap) => {
            if (seenIds.has(docSnap.id)) return;
            seenIds.add(docSnap.id);
            videoResults.push(createDocRecord(docSnap));
          });
        };

        if (resolvedStudentId) {
          appendDocs(await getDocs(query(collection(db, 'videos'), where('userId', '==', resolvedStudentId))));
          appendDocs(await getDocs(query(collection(db, 'videos'), where('studentId', '==', resolvedStudentId))));

          const userSnap = await getDoc(doc(db, 'users', resolvedStudentId));
          if (userSnap.exists()) {
            setStudentName((current) => current || String(userSnap.data()?.name || '').trim());
            setStudentEmail((current) => current || String(userSnap.data()?.email || '').trim());
          } else {
            setStudentName((current) => current || '');
            setStudentEmail((current) => current || '');
          }
        }

        if (baseVideo && !seenIds.has(baseVideo.id)) {
          videoResults.push(baseVideo);
        }

        const sortedVideos = videoResults.sort((a, b) => {
          const aTime = a.reviewedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.reviewedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        const nextFeedbackMap = {};
        await Promise.all(
          sortedVideos.map(async (video) => {
            if (video.status !== 'reviewed') return;
            const feedbackSnap = await getDocs(collection(db, 'videos', video.id, 'timestampedFeedback'));
            nextFeedbackMap[video.id] = feedbackSnap.docs
              .map((feedbackDoc) => feedbackDoc.data())
              .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          })
        );

        setStudentId(resolvedStudentId);
        setVideos(sortedVideos);
        setFeedbackMap(nextFeedbackMap);
        setSelectedSport((current) => current || resolvedSport || getDominantSport(sortedVideos) || 'skiing');
        if (loadedLesson) {
          setLessonPlan(loadedLesson);
        }
      } catch (error) {
        console.error('Error loading instructor lesson builder:', error);
        setLoadError('The lesson builder could not load this lesson context safely.');
      } finally {
        setLoading(false);
      }
    };

    fetchLessonContext();
  }, [currentUser?.uid, queryLessonId, querySkillId, querySport, queryStudentId, queryVideoId]);

  const sportVideos = useMemo(
    () => videos.filter((video) => normalizeSportName(video?.sport || '') === selectedSport),
    [selectedSport, videos]
  );
  const summary = useMemo(
    () => buildStudentProgressSummary(sportVideos, { feedbackMap }),
    [feedbackMap, sportVideos]
  );
  const availableSkills = useMemo(() => getProgressionSkillsForSport(selectedSport), [selectedSport]);

  useEffect(() => {
    const validSkillIds = availableSkills.map((skill) => skill.skillId);
    if (validSkillIds.length === 0) {
      setSelectedSkillId('');
      return;
    }

    const recommendedSkillId = buildInstructorLessonPlan({
      videoId: queryVideoId,
      studentId,
      instructorId: currentUser?.uid || '',
      studentName,
      studentEmail,
      sport: selectedSport,
      summary,
    }).targetSkillId;
    if (!hasManualSkillSelection && recommendedSkillId && validSkillIds.includes(recommendedSkillId) && selectedSkillId !== recommendedSkillId) {
      setSelectedSkillId(recommendedSkillId);
      return;
    }

    const nextSkillId =
      (querySkillId && validSkillIds.includes(querySkillId) && querySkillId) ||
      validSkillIds[0];

    if (!selectedSkillId || !validSkillIds.includes(selectedSkillId)) {
      setSelectedSkillId(nextSkillId);
    }
  }, [
    availableSkills,
    currentUser?.uid,
    hasManualSkillSelection,
    querySkillId,
    queryVideoId,
    selectedSkillId,
    selectedSport,
    studentId,
    studentEmail,
    studentName,
    summary,
  ]);

  useEffect(() => {
    if (!selectedSport) return;
    const nextPath = buildInstructorLessonPath({
      videoId: queryVideoId,
      studentId,
      sport: selectedSport,
      skill: selectedSkillId,
      lessonId: lessonInstanceId,
    });
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [lessonInstanceId, location.pathname, location.search, navigate, queryVideoId, selectedSkillId, selectedSport, studentId]);

  useEffect(() => {
    setLessonPlan((current) =>
      buildInstructorLessonPlan({
        videoId: queryVideoId,
        studentId,
        instructorId: currentUser?.uid || '',
        studentName,
        studentEmail,
        sport: selectedSport,
        targetSkillId: selectedSkillId,
        summary,
        existingLesson:
          current?.sport === selectedSport && current?.targetSkillId === selectedSkillId
            ? current
            : null,
      })
    );
  }, [currentUser?.uid, queryVideoId, selectedSkillId, selectedSport, studentEmail, studentId, studentName, summary]);

  const nextSkillOptions = useMemo(() => {
    if (!selectedSport) return [];
    return getProgressionSkillsForSport(selectedSport);
  }, [selectedSport]);

  const canUseAI = canUseAIAssistDrafts(userData || {}, userData?.orgId || '');

  const handleApplyAiLessonSuggestion = (aiResult) => {
    (aiResult.stages || []).forEach(({ stage, coachNotes }) => {
      if (stage && coachNotes) {
        handleStageChange(stage, 'coachNotes', `[AI suggestion] ${coachNotes}`);
      }
    });
  };

  const handleStageChange = (stageKey, field, value) => {
    setSaveMessage('');
    setSaveError('');
    setLessonPlan((current) => {
      if (!current) return current;
      return {
        ...current,
        stages: current.stages.map((stage) =>
          stage.stage === stageKey ? { ...stage, [field]: value } : stage
        ),
      };
    });
  };

  const handleOutcomeChange = (field, value) => {
    setSaveMessage('');
    setSaveError('');
    setLessonPlan((current) => {
      if (!current) return current;
      return {
        ...current,
        lessonOutcome: {
          ...current.lessonOutcome,
          [field]: value,
        },
      };
    });
  };

  const persistLesson = async (status) => {
    if (!lessonPlan || !currentUser?.uid || saving) return;

    setSaving(true);
    setSaveError('');
    setSaveMessage('');
    try {
      const isExistingLesson = Boolean(lessonInstanceId);

      // Pass 70B — for a NEW standalone lesson, resolve the two-ID model from REAL org membership, never from
      // currentUser.uid: the canonical BUSINESS instructorId = the teaching membership's userId; the Auth uid
      // stays separate. Multi-org is ambiguous (this screen has no org selector) → fail closed, do not guess.
      let targetOrg = '';
      let actorInstructorId = '';
      if (!isExistingLesson) {
        const teachingMemberships = getOrganizationMemberships(userData || {}).filter(isTeachingMembership);
        if (teachingMemberships.length !== 1) {
          setSaveError(
            teachingMemberships.length === 0
              ? 'Your account is not set up to deliver lessons for an organisation, so a new lesson cannot be created here.'
              : 'Your account teaches in more than one organisation. A new standalone lesson needs an explicit organisation, which this screen does not select yet.'
          );
          setSaving(false);
          return;
        }
        targetOrg = teachingMemberships[0].organizationId;
        actorInstructorId = teachingMemberships[0].userId;
      }

      // Stable per-draft id (generated once, reused on retry) → idempotent create.
      let targetLessonId = lessonInstanceId;
      if (!targetLessonId) {
        targetLessonId = draftLessonId || doc(collection(db, 'lessonInstances')).id;
        if (!draftLessonId) setDraftLessonId(targetLessonId);
      }

      const result = await saveLessonBuilderLesson(db, {
        lessonInstanceId: targetLessonId,
        lessonPlan: {
          // coaching/delivery content only — the writer preserves stored identity/lineage on an existing lesson.
          ...lessonPlan,
          videoId: queryVideoId,
          studentId,
          studentName,
          studentEmail,
          sport: selectedSport,
          targetSkillId: selectedSkillId,
        },
        status,
        userData,
        actorAuthUserId: currentUser.uid,
        actorInstructorId,
        organizationId: targetOrg,
      });

      if (!lessonInstanceId) setLessonInstanceId(result.lessonInstanceId);

      setLessonPlan((current) =>
        current
          ? {
              ...current,
              status,
            }
          : current
      );
      setSaveMessage(status === 'completed' ? 'Lesson saved as completed.' : 'Lesson draft saved.');
    } catch (error) {
      console.error('Error saving lesson instance:', error);
      setSaveError('We could not save this lesson right now. Your edits are still on screen.');
    } finally {
      setSaving(false);
    }
  };

  const recommendedSkillName =
    summary?.nextSkill?.name ||
    summary?.currentSkill?.name ||
    (lessonPlan?.recommendedBySystem ? lessonPlan.targetSkillName : '') ||
    'No recommendation yet';
  const activeTrackName = lessonPlan?.trackName || summary?.activeTrack?.name || 'Core Progression';
  const effectiveReason =
    lessonPlan?.recommendationReason ||
    'The app will show a review-based recommendation here once the student has enough reviewed progress in this sport.';

  return (
    <div className="mx-auto max-w-6xl p-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Instructor Lesson Builder</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-950">Plan A Skill-Driven Lesson</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Start from the student&apos;s current recommendation, then adjust the target skill, lesson stages, and outcome notes to fit what you actually teach.
        </p>
        <p className="mt-2 max-w-3xl text-sm text-slate-500">
          The system recommends a focus and lesson flow. You stay in control of the final lesson.
        </p>
        {loadError ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {loadError}
          </div>
        ) : null}

        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Loading lesson context...</p>
        ) : (
          <>
            <div className="mt-6 grid gap-4 md:grid-cols-4">
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Student</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{studentName || studentEmail || studentId || 'Unassigned client'}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recommended Skill</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{recommendedSkillName}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active Track</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{activeTrackName}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current Level</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{summary?.currentLevel || 'beginner'}</p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="lesson-student-name">
                  Student Name
                </label>
                <input
                  id="lesson-student-name"
                  value={studentName}
                  onChange={(event) => setStudentName(event.target.value)}
                  placeholder="Optional if this lesson is being created before the student joins"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="lesson-student-email">
                  Student Email
                </label>
                <input
                  id="lesson-student-email"
                  type="email"
                  value={studentEmail}
                  onChange={(event) => setStudentEmail(event.target.value)}
                  placeholder="Optional email for future client access/invite flow"
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              {SPORT_OPTIONS.map((sport) => {
                const isActive = selectedSport === sport.key;
                return (
                  <button
                    key={sport.key}
                    type="button"
                    onClick={() => {
                      setSelectedSport(sport.key);
                      setSelectedSkillId('');
                      setHasManualSkillSelection(false);
                    }}
                    className={`rounded-xl border px-4 py-2 text-sm font-medium ${
                      isActive
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {sport.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Recommendation Reason</p>
              <p className="mt-2 text-sm text-indigo-900">{effectiveReason}</p>
            </div>
            {saveMessage ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                {saveMessage}
              </div>
            ) : null}
            {saveError ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
                {saveError}
              </div>
            ) : null}

            <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_420px]">
              <section className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div className="flex-1">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="lesson-skill-select">
                      Target Skill
                    </label>
                    <select
                      id="lesson-skill-select"
                      value={selectedSkillId}
                      onChange={(event) => {
                        setSelectedSkillId(event.target.value);
                        setHasManualSkillSelection(true);
                      }}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    >
                      {availableSkills.map((skill) => (
                        <option key={skill.skillId} value={skill.skillId}>
                          {skill.name} ({formatLabel(skill.level)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plan State</p>
                    <p className="mt-1 text-sm font-medium text-slate-900">
                      {lessonPlan?.recommendedBySystem ? 'Using the system recommendation' : 'Instructor-adjusted skill'}
                    </p>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected Skill</p>
                  <h2 className="mt-2 text-lg font-semibold text-slate-950">{lessonPlan?.targetSkillName || 'Choose a skill'}</h2>
                  <p className="mt-2 text-sm text-slate-600">{lessonPlan?.targetSkillDescription || 'Pick a skill to load the lesson structure.'}</p>
                </div>

                <div className="mt-5 space-y-4">
                  {lessonPlan?.stages?.map((stage) => (
                    <section key={stage.stage} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stage.title || formatLabel(stage.stage)}</p>
                          {(stage.plannedDrills || []).length > 0 ? (
                            <ul className="mt-3 space-y-1 text-sm text-slate-700">
                              {(stage.plannedDrills || []).map((drill) => (
                                <li key={drill.drillId}>{drill.name}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="mt-3 text-sm text-slate-500">No drills loaded for this stage yet.</p>
                          )}
                        </div>

                        <div className="w-full max-w-[180px]">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor={`status-${stage.stage}`}>
                            Completion
                          </label>
                          <select
                            id={`status-${stage.stage}`}
                            value={stage.completionStatus}
                            onChange={(event) => handleStageChange(stage.stage, 'completionStatus', event.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                          >
                            {LESSON_STAGE_COMPLETION_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {formatLabel(option)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor={`notes-${stage.stage}`}>
                          Instructor Notes
                        </label>
                        <textarea
                          id={`notes-${stage.stage}`}
                          value={stage.instructorNotes}
                          onChange={(event) => handleStageChange(stage.stage, 'instructorNotes', event.target.value)}
                          placeholder="Add any coaching notes, adjustments, or terrain changes for this stage."
                          className="mt-2 min-h-[88px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-900"
                        />
                      </div>
                    </section>
                  ))}

                  {!lessonPlan?.hasLessonFlow && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                      {lessonPlan?.lessonFlowEmptyText}
                    </div>
                  )}
                </div>
              </section>

              <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-5 xl:sticky xl:top-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Lesson Outcome</p>
                <h2 className="mt-2 text-lg font-semibold text-slate-950">Record What Happened</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Close the loop with a simple lesson outcome so the next recommendation can become more useful over time.
                </p>

                <div className="mt-5 space-y-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="lesson-status">
                      Lesson Status
                    </label>
                    <select
                      id="lesson-status"
                      value={lessonPlan?.lessonOutcome?.status || 'in_progress'}
                      onChange={(event) => handleOutcomeChange('status', event.target.value)}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    >
                      {LESSON_OUTCOME_STATUS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {formatLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="skill-confidence">
                      Skill Confidence
                    </label>
                    <select
                      id="skill-confidence"
                      value={lessonPlan?.lessonOutcome?.skillConfidence || 'developing'}
                      onChange={(event) => handleOutcomeChange('skillConfidence', event.target.value)}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    >
                      {LESSON_SKILL_CONFIDENCE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {formatLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="recommended-next-skill">
                      Recommended Next Skill
                    </label>
                    <select
                      id="recommended-next-skill"
                      value={lessonPlan?.lessonOutcome?.recommendedNextSkillId || ''}
                      onChange={(event) => handleOutcomeChange('recommendedNextSkillId', event.target.value)}
                      className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                    >
                      <option value="">Keep current recommendation</option>
                      {nextSkillOptions.map((skill) => (
                        <option key={skill.skillId} value={skill.skillId}>
                          {skill.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="lesson-summary">
                      Instructor Summary
                    </label>
                    <textarea
                      id="lesson-summary"
                      value={lessonPlan?.lessonOutcome?.instructorSummary || ''}
                      onChange={(event) => handleOutcomeChange('instructorSummary', event.target.value)}
                      placeholder="Summarize what the student managed well, what still needs work, and how you would aim the next lesson."
                      className="mt-2 min-h-[120px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900"
                    />
                  </div>
                </div>

                {canUseAI && studentId ? (
                  <div className="mt-6">
                    <AiCopilotPanel
                      draftType="lesson_plan"
                      signals={buildClientSignalsFromWorkspace(
                        {
                          clientProfile: {
                            userId: studentId,
                            displayName: studentName || studentEmail || studentId,
                            primaryDiscipline: selectedSport,
                          },
                          videos,
                          lessons: [],
                          bookings: [],
                          feedbackHistory: [],
                          packageSummary: { activeMemberships: [], servicePackages: [] },
                          clientNotes: [],
                        },
                        userData?.orgId || ''
                      )}
                      initialQuickInput={{ targetSkillId: selectedSkillId }}
                      applyFn={(aiResult) => handleApplyAiLessonSuggestion(aiResult)}
                      onDiscard={() => {}}
                    />
                  </div>
                ) : null}

                <div className="mt-6 rounded-2xl border border-slate-200 bg-white px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">MVP Boundary</p>
                  <p className="mt-2 text-sm text-slate-600">
                    This lesson builder now saves lightweight lesson instances in Firestore, while the underlying progression content and recommendation engine remain local app-layer systems.
                  </p>
                </div>

                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => persistLesson('draft')}
                    disabled={saving}
                    className="flex-1 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button
                    type="button"
                    onClick={() => persistLesson('completed')}
                    disabled={saving}
                    className="flex-1 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : 'Complete Lesson'}
                  </button>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InstructorLessonBuilderPage;
